import { type AnnotationType } from '@solink/vd-data-layer/api-clients/callhome/types';
import { type MotionSensitivity } from '@solink/vd-data-layer/api-clients/vms/vmsTypes';
import { type AlarmContactDto } from '@solinkcorp/callhome';
import settings from '@solinkcorp/settings';
import {
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
  type HistogramRange,
  WIDGET_TYPE_CAMERA_GROUP,
} from '@solinkcorp/vd-common';
import {
  type AlarmContactType,
  type AlarmProviderType,
  ScheduledActivityType,
} from 'clients/CallhomeApiTypes';
import type { ValueOf } from 'type-fest';
const defaultPath = '/events';

export const EVENTS_ROUTE = '/events';
export const ALL_EVENTS_ROUTE = '/events/all';
export const DELIVERY_EVENTS_ROUTE = '/events/delivery';
export const AGENTS_URL = '/agents';
export const DATASETS_URL = '/datasets';
export const DATA_EXPLORER_URL = '/data-explorer';
/**
 * Standalone Data Explorer VerifEye reviewer tab (SL-15900). A distinct top-level
 * path (not a `/data-explorer` sub-route) so it is reachable by a pure reviewer who
 * has no Data Explorer base access, and so tab-name resolution / routing never
 * collide with the DE route tree.
 */
export const DATA_EXPLORER_VERIFEYE_URL = '/data-explorer-verifeye';
export const INVESTIGATIONS_URL = '/investigations';
export const DEFAULT_TIMEZONE = 'America/Toronto';

export const ALL_FEATURE = 'all';
export const DELIVERY_FEATURE = 'delivery';
export const INVALID_FEATURE = 'invalid';
export const ALARM_FEATURE = 'alarm';
export const SCHEDULED_FEATURE = 'scheduled';
export const ARCHIVED_FEATURE = 'archived';
export const CLIPS_FEATURE = 'clips';
export const AI_FEATURE = 'ai';

// Tags
export const HIDE_FROM_DISPLAY_TAG = 'hideFromDisplay';
export const EXCLUDE_FROM_COUNT_TAG = 'excludeFromCount';
export const ARCHIVED_TAG = 'archived';

export const ALARM = 'ALARM';

export const NOTIFICATIONS_FILTER_BY_STATE = {
  all: 'ALL',
  read: 'READ',
  unread: 'UNREAD',
  archived: 'ARCHIVED',
  error: 'ERROR',
};

export const NOTIFICATIONS_FILTER_BY_TYPE = {
  alarm: 'ALARM',
  clip: 'CLIP',
  event: 'EVENT',
};

export const NOTIFICATIONS_FILTER_BY_SUBTYPE = {
  bookmark: 'BOOKMARK',
  motion: 'MOTION',
  transaction: 'TRANSACTION',
};

export const NOTIFICATIONS_CUTOFF = {
  archive: 'notifArchiveCutoff',
  read: 'notifReadCutoff',
};

export const NOTIFICATIONS_ANALYTICS_ACTION_TYPE = {
  all: 'all',
  bulk: 'bulk',
  single: 'single',
};

export const NOTIFICATIONS_MAX_QUERY_LIMIT = 1000;

export const NOTIFICATIONS_MIN_QUERY_DATE = 1590322374000;

export const DEFAULT_NOTIFICATIONS_QUERY = {
  collection: 'users',
  limit: NOTIFICATIONS_MAX_QUERY_LIMIT,
  orderBy: ['created', 'desc'],
  storeAs: 'notifications',
  subcollections: [{ collection: 'notifications' }],
  where: [
    ['created', '>', NOTIFICATIONS_MIN_QUERY_DATE],
    ['state', 'in', [NOTIFICATIONS_FILTER_BY_STATE.unread, NOTIFICATIONS_FILTER_BY_STATE.read]],
  ],
};

export const EMPTY_WEEK_SCHEDULE = {
  0: [],
  1: [],
  2: [],
  3: [],
  4: [],
  5: [],
  6: [],
};
export const START_OF_DAY = '00:00';
export const END_OF_DAY = '23:59';
export const FULL_DAY_PERIOD = {
  start: START_OF_DAY,
  end: END_OF_DAY,
};
export const FULL_WEEK_SCHEDULE = {
  0: [FULL_DAY_PERIOD],
  1: [FULL_DAY_PERIOD],
  2: [FULL_DAY_PERIOD],
  3: [FULL_DAY_PERIOD],
  4: [FULL_DAY_PERIOD],
  5: [FULL_DAY_PERIOD],
  6: [FULL_DAY_PERIOD],
};
export const DAYS_IN_WEEK = 7;

export const DOES_NOT_REPEAT = 'none';
export const REPEAT_EVERY_15MIN = '15min';
export const REPEAT_EVERY_30MIN = '30min';
export const REPEAT_HOURLY = 'hourly';
export const REPEAT_EVERY_4H = '4th-hour';
export const REPEAT_EVERY_6H = '6th-hour';
export const REPEAT_DAILY = 'daily-nth-day';
export const REPEAT_WEEKLY = 'weekly-nth-day';
export const REPEAT_MONTHLY = 'monthly-nth-day';
export const REPEAT_ANNUALLY = 'annually';
export const REOCCUR_INDEFINITE = -1;
export const REOCCUR_NEVER_END = -1;
export const OCCUR_ONCE = 1;
export const OCCUR_NEVER = 0;
export const REPEAT_RULE_TYPE = 'CRON' as const;
/** 24 repeats for 31 days */
export const MAX_HOURLY_OCCURRENCES_PER_MONTH = 24 * 31;
/** activities are valid for 2 years */
export const MAX_HOURLY_OCCURRENCES_PER_2_YEARS = 24 * 31 * 12 * 2;

export const HOME_NON_EXPORTABLE_WIDGETS = [WIDGET_TYPE_CAMERA_GROUP];
export const NON_FETCHABLE_WIDGETS = [WIDGET_TYPE_CAMERA_GROUP];
// Master time only ticks once a second, so an event that starts in less that
// one second from the current master time is considered "current".
export const MASTER_TIME_RESOLUTION = 1000;
export const CLICK_DELAY = 200;
export const WIDGET_FETCH_TIMEOUT = 30010;
export const DEFAULT_LOCALIZED_DATE_FORMAT = 'PP';
export const RESET_URLS = [defaultPath, '/login', '/'];
export const FAST_NOTIFICATION_DURATION = 2000;
export const MEDIUM_NOTIFICATION_DURATION = 4000;
export const LONG_NOTIFICATION_DURATION = 8000;
export const GRID_GAP_2PX = '2px';
export const GRID_GAP_4PX = '4px';
export const JWT = 'jwt';
export const DEVICE_ID = 'deviceId';
export const LOCATION_ID = 'locationId';
export const TENANT_ID = 'tenantId';
export const MAX_FLOORPLAN_FILE_SIZE_MB = 20;
export const MAX_FLOORPLAN_FILE_SIZE = MAX_FLOORPLAN_FILE_SIZE_MB * 1024 * 1024;
export const STATUS_SUCCEEDED = 'SUCCEEDED';
export const STATUS_FAILED = 'FAILED';
export type Status = typeof STATUS_SUCCEEDED | typeof STATUS_FAILED;

// navigation
export const TAB_ALARMS = 'Alarms';
export const TAB_DASHBOARD = 'Dashboard';
export const TAB_EVENTS = 'Events';
export const TAB_VIDEO = 'Video';
export const TAB_CLIPS = 'Clips';
export const TAB_SIDEKICK = 'Sidekick';

//num events per quer;
export const WEB_QUERY_COUNT = 30;
export const TABLE_QUERY_COUNT = 100;
export const QUERY_COUNT = 10;
export const EXPORT_QUERY_COUNT = 1000;
export const MAX_LIVE_CAM_COUNT = 16;
export const MAX_ARCHIVE_CAM_COUNT = 6;
export const SD_RESTRICTION_THRESHOLD = 6;
export const MAX_EVENT_DESCRIPTION_LENGTH = 300;
export const VPN_TEST_THRESHOLD = 0.5;
export const VPN_TEST_SAMPLE_SIZE = 6;

// TODO For now max limit for users is 100, until we e.g. update platform to make multiple calls to auth0 (which has a max limit of 100 per page)
export const MAX_USERS = 100;
export const LOCATION_PAGE_SIZE = 20;
export const MULTI_VIEW_SYNC_THRESHOLD = 1;
export const CAMERA_THUMBNAIL_WIDTH = 320;
export const BASE_EVENT_HEIGHT = 250;
export const EVENT_CARD_WIDTH = 320;
export const EVENT_CARD_WIDTH_MARGIN = 5;
export const EVENT_CARD_VERTICAL_MARGIN = 10;
export const EVENT_CARD_THUMBNAIL_HEIGHT = 180;
export const EVENT_CARD_DATA_ONLY_HEADER_HEIGHT = 30;
export const EVENT_LIST_MIN_PADDING = 14;
export const SIDE_NAV_WIDTH = 80;
export const RECEIPT_LINE_HEIGHT = 14;
export const ALARM_RECEIPT_ENTRY_MARGIN = 3;
export const VIEW_PORT_WINDOW = 30;
export const DEFAULT_TIME_FORMAT = 'locale';
export const PUSH_STATUS_CLOUD = '102';
export const PUSH_STATUS_LOCAL = '100';
export const VIDEO_ASPECT_WIDTH = 16;
export const VIDEO_ASPECT_HEIGHT = 9;
export const VIDEO_ASPECT_RATIO = VIDEO_ASPECT_WIDTH / VIDEO_ASPECT_HEIGHT;
export const MIN_ZOOM_LEVEL = 1;
export const MAX_ZOOM_LEVEL = 8;
export const MIN_MOTION_DURATION_THRESHOLD_S = 30;
export const MAX_MOTION_DURATION_THRESHOLD_S = 300;
export const MAX_MOTION_INTENSITY_THRESHOLD = 3;
export { SETTINGS_PAGE_URL, STANDARD_12H_DATE_TIME_FORMAT, TITLE } from '@solinkcorp/constants';
export const WARNING_HOURS_AFTER_LOCAL_RETENTION = 6;
export const BASE_64_VALUE_1 = 'AVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU';
export const REQUEST_TIMEOUT = 30 * 1000;
export const TEN_SECONDS = 10 * 1000;
export const LOCAL_FILTERS_EXPIRATION = 14400000; // 4HRs in ms
export const CONNECT_HLS = 'Connect HLS';
export const HLS = 'HLS';

// Keyboard keys
export const KEYS = {
  UP_KEY: {
    // Key values from KeyboardEvent.key (https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values)
    name: 'ArrowUp',
    // Key code value (https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode)
    value: 38,
    // Indicates whether the Shift key also had to be pressed, e.g. when typing a '?' (question mark)
    shiftRequired: false,
    // Alternative key values (NOT key code values) used by older browser versions
    // (E.g. https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values#Navigation_keys)
    alternatives: ['Up'],
  },
  DOWN_KEY: {
    name: 'ArrowDown',
    value: 40,
    shiftRequired: false,
    alternatives: ['Down'],
  },
  TAB_KEY: {
    name: 'Tab',
    value: 9,
    shiftRequired: false,
  },
  ENTER_KEY: {
    name: 'Enter',
    value: 13,
    shiftRequired: false,
  },
  BACKSPACE_KEY: {
    name: 'Backspace',
    value: 8,
    shiftRequired: false,
  },
  ESC_KEY: {
    name: 'Escape',
    value: 27,
    shiftRequired: false,
    alternatives: ['Esc'],
  },
  SPACE_KEY: {
    name: ' ',
    value: 32,
    shiftRequired: false,
    alternatives: ['Spacebar'],
  },
  EQUAL_KEY: {
    name: '=',
    value: 187,
    shiftRequired: false,
  },
  LESS_THAN_KEY: {
    name: '<',
    value: 188,
    shiftRequired: true,
  },
  GREATER_THAN_KEY: {
    name: '>',
    value: 190,
    shiftRequired: true,
  },
  HASH_KEY: {
    name: '#',
    value: 51,
    shiftRequired: true,
  },
  COMMA_KEY: {
    name: ',',
    value: 188,
    shiftRequired: false,
  },
  SEMICOLON_KEY: {
    name: ';',
    value: 186,
    shiftRequired: false,
  },
};

// events
// - used to avoid fetching events "too new" where thumbnails may not yet be processed
// - TODO Improvements: use this constant in other places
export const EVENTS_BLACK_OUT_WINDOW_IN_MINUTES = 10;
export const ITEM_SHOW_MS = 2000;
export const STANDARD_DATE_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_NOON_TIME_OF_DAY_FILTER = 12; // Used by moment.js where hours are indexed 0-23
export const MINIMUM_PINNED_PLAY_TIME_MS = 4000;
export const EVENT_TYPES = {
  TRANSACTION: EVENT_TYPE_TRANSACTION,
  MOTION: EVENT_TYPE_MOTION,
  BOOKMARK: EVENT_TYPE_BOOKMARK,
};
export const UNSUPPORTED_FIELD_TYPES = ['date', 'string'];
export const EVENT_TYPES_ARRAY = Object.values(EVENT_TYPES);
export const EVENT_TYPES_FILTER = {
  ALL: 'all',
  REVENUE: 'revenue',
  ...EVENT_TYPES,
};
export const OWNER_TYPE = {
  USER: 'user',
  CUSTOMER: 'customer',
  RESELLER: 'reseller',
  CLOUD: 'cloud',
  SOLINK: 'solink',
} as const;
export const EVENT_GRID_RENDER_TYPES = {
  DATE: 'DATE_SEPARATOR',
  EVENT: 'EVENT',
};
export const DATA_CATEGORY_ALL = 'all';
export const DATA_CATEGORY_OTHER = 'other';

// widgets
export const WIDGETS_VIEW_SETTINGS = 'WIDGETS_VIEW_SETTINGS';
export const RANKING_WIDGETS_VIEW_SETTINGS = 'RANKING_WIDGETS_VIEW_SETTINGS';
export const PERCENT_THRESHOLD_VIEW_SETTINGS = 'PERCENT_THRESHOLD_VIEW_SETTINGS';
export const UPDATE_HISTOGRAM_CUSTOM_FIELDS = 'UPDATE_HISTOGRAM_CUSTOM_FIELDS';
export const WIDGET_REFRESH_SHORT_INTERVAL_MS = 60 * 1000;
export const WIDGET_REFRESH_LONG_INTERVAL_MS = 5 * 60 * 1000;
export const WIDGET_UI_OWNER_TYPE = {
  PUBLIC: 'public',
  PRIVATE: 'private',
};

// users
export const FIRST_LAST_NAME_MAX_LENGTH = 25;
export const NAME_MAX_LENGTH = 25;
export const MAX_ASSIGNABLE_LOCATIONS = 20;
export const USERS_REQUEST_LIMIT = 200;
export const PASSWORD_MIN_LENGTH = 8;
export const USER_ROLE_NAME_MAX_LENGTH = 100;
export const USER_ROLE_DESCRIPTION_MAX_LENGTH = 1000;
export const VIDEO_ALARM_TABLET_USERS = 'Video Alarm Tablet';

// search filter reports
export const MAX_LENGTH_REPORT_NAME = 50;
export const MAX_LENGTH_REPORT_DESCRIPTION = 500;
export const INHERIT_TYPE_ASSIGNED = 'assigned';
export const INHERIT_TYPE_ALL = 'all';

// dispatch
export const DISPATCH_TIMEOUT_MS = 0;

// keys
export const LOAD_MORE = 'load-more';

// idle time out
export const IDLE_TIME_OUT = 15;

// polling
export const POLLING_DEFAULT_MAX_RETRY_ATTEMPTS = 5;
export const POLLING_DEFAULT_INTERVAL_MS = 3000;
export const POLLING_DEFAULT_RETRY_INTERVAL_MS = 20000;
export const POLLING_DOWNLOAD_STATUS_ERROR = 'error';

// time formats
export { TIME_FORMAT } from '@solinkcorp/constants';

export type { TimeFormat } from '@solinkcorp/authentication';

// downloads
export const VIDEO_DOWNLOAD_TYPES = ['mp4', 'avi', 'ts', 'mkv'] as const;
export const VIDEO_SOURCE_OPTIONS = ['cloudfirst', 'localfirst', 'cloudonly', 'localonly'];
export const AUTO_DOWNLOAD_TIMEOUT_MS = 500;
export const DOWNLOAD_EXPIRATION_TIME = 30 * 24 * 60 * 60 * 1000;

// shares
export const MAX_LENGTH_CLIP_TITLE = 100;
export const MAX_LENGTH_SHARE_MESSAGE = 300;
export const SHARE_EXPIRATION_DAYS = 30;
export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
export const VIDEO_SOURCE_LOCAL = 'local';
export const VIDEO_SOURCE_CLOUD = 'cloud';
export const DEFAULT_PUSH_PRIORITY = 150;
export const CLOUD_DOWNLOAD_MP4 = 'mp4' as const;
export const CLOUD_DOWNLOAD_ZIP = 'zip' as const;

export type VideoSource = typeof VIDEO_SOURCE_LOCAL | typeof VIDEO_SOURCE_CLOUD;

// push-to-cloud share status
export const SHARE_STATUS_ENABLED = 'open' as const;
export const SHARE_STATUS_DISABLED = 'closed' as const;

export type ShareStatus = typeof SHARE_STATUS_ENABLED | typeof SHARE_STATUS_DISABLED;
// push-to-cloud status
export const CLOUD_STATUS_IN_PROGRESS = 'inProgress';
export const CLOUD_STATUS_COMPLETE = 'complete';
export const CLOUD_STATUS_ERROR = 'error';
export const CLOUD_STATUS_COMPLETE_WITH_ERRORS = 'completeWithErrors';
export const CLOUD_STATUS_CANCELLED = 'cancelled';
export const CLOUD_STATUS_VERIFY = 'verify';

export type PushToCloudStatus =
  | typeof CLOUD_STATUS_IN_PROGRESS
  | typeof CLOUD_STATUS_COMPLETE
  | typeof CLOUD_STATUS_ERROR
  | typeof CLOUD_STATUS_COMPLETE_WITH_ERRORS
  | typeof CLOUD_STATUS_CANCELLED
  | typeof CLOUD_STATUS_VERIFY;

// connect download status
export const CONNECT_DOWNLOAD_STATUS_ENQUEUED = 'enqueued';
export const CONNECT_DOWNLOAD_STATUS_PROCESSING = 'processing';
export const CONNECT_DOWNLOAD_STATUS_READY = 'ready';
export const CONNECT_DOWNLOAD_STATUS_DOWNLOADED = 'downloaded';
export const CONNECT_DOWNLOAD_STATUS_FAILED = 'failed';
export const CONNECT_DOWNLOAD_STATUS_CANCELLED = 'cancelled';
export const CONNECT_DOWNLOAD_STATUS_EXPIRED = 'expired';

export type ConnectDownloadStatus =
  | typeof CONNECT_DOWNLOAD_STATUS_ENQUEUED
  | typeof CONNECT_DOWNLOAD_STATUS_PROCESSING
  | typeof CONNECT_DOWNLOAD_STATUS_READY
  | typeof CONNECT_DOWNLOAD_STATUS_DOWNLOADED
  | typeof CONNECT_DOWNLOAD_STATUS_FAILED
  | typeof CONNECT_DOWNLOAD_STATUS_CANCELLED
  | typeof CONNECT_DOWNLOAD_STATUS_EXPIRED;

// search
export const SEARCH_TYPE_STRING = 'string';
export const SEARCH_TYPE_NUMBER = 'number';
export const SEARCH_TYPE_DATE = 'date';

// digest notification period
export const DIGEST_NONE = 'none';
export const DIGEST_DAILY = 'daily';
export const DIGEST_WEEKLY = 'weekly';

// real-time notifications
export const REAL_TIME_NOTIFICATION_THRESHOLD_PUSH = 1000;
export const REAL_TIME_NOTIFICATION_THRESHOLD_SMS = 100;
export const REAL_TIME_NOTIFICATION_THRESHOLD_EMAIL = 100;

// table view
export const START_TIME_LOCAL = 'startTimeLocal';
export const TABLEVIEW_DEFAULT_SORTING_FIELD = START_TIME_LOCAL;
export const TABLEVIEW_MODE = false;
export const TABLEVIEW_STANDARD_FIELDS = [
  { id: TABLEVIEW_DEFAULT_SORTING_FIELD, sort: 'desc' },
  { id: 'title' },
  { id: 'subtitle' },
  { id: 'details.Total amount' },
  { id: 'details.Number items entered' },
] as const;
export const TABLEVIEW_DEFAULT_SORTING = {
  isDescending: true,
  prop: TABLEVIEW_DEFAULT_SORTING_FIELD,
};

// cameras
export const FAVOURITE_CAMERAS_GROUP_NAME = 'Favorites'; // defined by CallHome
export const FAVOURITE_CAMERA_GROUP_TRANSLATIONS = [
  'Favorites',
  'Favourites',
  'Favoritos',
  'Favoris',
  '收藏',
];
export const RECENT_CAMERAS_GROUP_NAME = 'Recently Viewed'; // defined by CallHome
export const RECENT_CAMERAS_GROUP_LIMIT = 16;
export const RECENT_CAMERA_GROUP_TRANSLATIONS = [
  'Recently Viewed',
  'Vistas recientemente',
  'Vues récemment',
  '最近播放',
];
export const DATA_SOURCE_MOTION = 'Motion';

export const CAMERA_ANNOTATION_TYPE_DATA_SOURCE = 'Data source';
export const CAMERA_ANNOTATION_TYPE_DOOR = 'Door';

export const CAMERA_ANNOTATION_OBJECT_DOOR = {
  type: CAMERA_ANNOTATION_TYPE_DOOR,
  color: '#ff5500',
  label: 'Door',
  en: { label: 'Door' },
  fr: { label: 'Porte' },
  es: { label: 'Puerta' },
};

export const DEFAULT_CAMERA_ANNOTATION_TYPES: AnnotationType[] = [
  {
    type: 'Table',
    color: '#55a946',
    label: 'Table',
    en: { label: 'Table' },
    fr: { label: 'Table' },
    es: { label: 'Mesa' },
  },
  {
    type: 'Data source',
    color: '#ff0000',
    label: 'Data source',
    en: { label: 'Data source' },
    fr: { label: 'La source de données' },
    es: { label: 'Fuente de datos' },
  },
];

export const ROI_EMPTY = 'AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=';

export const ALARM_SCHEDULE_LEVELS = {
  CAMERA: 'CAMERA',
  LOCATION: 'LOCATION',
  NONE: 'NONE',
};
export const CAMERA_GROUP_TYPE_STANDARD = 'STANDARD';
export const CAMERA_GROUP_TYPE_FLOORPLAN = 'FLOOR_PLAN';
export const CAMERA_GROUP_OWNER_TYPE_CUSTOMER = 'customer';
export const CAMERA_GROUP_TYPE_STANDARD_LOWERCASE = 'standard';
export const CAMERA_GROUP_TYPE_FLOORPLAN_LOWERCASE = 'floorplan';

// alarm
export const ALARM_PROVIDER_TYPES = {
  SOLINK: 'SOLINK',
  NOONLIGHT: 'NOONLIGHT',
  MONITOR_CENTER: 'MONITOR_CENTER',
  IMMIX: 'IMMIX',
} as const satisfies Record<AlarmProviderType, AlarmProviderType>;

export const ALARM_MODE = {
  LOITERING: 'LOITERING',
  STANDARD: 'STANDARD',
  DETERRENT: 'DETERRENT ONLY',
} as const;

export type AlarmMode = (typeof ALARM_MODE)[keyof typeof ALARM_MODE];

export const SELF_MONITORED_PLUS = 'SELF_MONITORED_PLUS';

export const ALARM_CONTACT_TYPES = {
  STANDARD: 'STANDARD',
  EXTERNAL: 'EXTERNAL',
  GUEST: 'GUEST',
  DTMFPIN: 'DTMFPIN',
  MANAGEMENT: 'MANAGEMENT',
} as const satisfies Record<AlarmContactType, AlarmContactType>;

export const DEVICE_ALARM_MODES = {
  ACTIVE: 'ACTIVE',
  DISABLED: 'DISABLED',
  TEST: 'TEST',
};
export const NO_TEMP_DISARM = 0;
export const NO_TEMP_ARM = 0;
export const DISARM_OPTION_A_MIN = 30;
export const DISARM_OPTION_B_MIN = 60;
export const ALARM_CONFIG_CARD_GAP = 24;
export const ALARM_PIN_ACCESS_NEVER_EXPIRE = -1;
export const PinType = {
  ARM_DISARM: 'ARM_DISARM',
  REMOTE_GUARD: 'REMOTE_GUARD',
} as const satisfies Record<string, NonNullable<AlarmContactDto['pinType']>>;
/** Derived from the generated callhome DTO so the API stays the source of truth. */
export type PinType = NonNullable<AlarmContactDto['pinType']>;
export const ALARM_LOCATION_MENU_LAYOUT = 'ALARM_LOCATION_MENU_LAYOUT';
export const ALARM_MIN_INTERLINK_MAJOR_VERSION = 37;
export const MAXIMUM_CONTACT_NOONLIGHT = 5;
export const SCHEDULED_INSPECTION_REASON = ScheduledActivityType.SCHEDULED_INSPECTION;
export const SCHEDULED_AI_CHAT_REASON = ScheduledActivityType.SCHEDULED_AI_CHAT;
export const AGENT_SUBTYPE = 'agent';
export const OPEN_AI_SUBTYPE = 'ai-openAI';
export const ALARM_EXCEPTION_REASON = 'Alarm schedule exception';
export const ALARM_EXCEPTION_START = '2021-11-01';
export const ALARM_EXCEPTION_DISABLE_EDIT_MINUTE = 5;
export const RECENT_ALARMS_REFRESH_INTERVAL = 15 * 1000;
export const DEFAULT_MAXIMUM_ALARM_CAMERAS = 5;
export const AI_ZONETRACKING_SUBTYPE = 'ai-zoneTracking';
export const AI_LINECROSSING_SUBTYPE = 'ai-lineCrossing';

export const RECENT_ALARM_EVENTS = 'RECENT_ALARM_EVENTS';

// floor plans
export const TYPE_MARKER = 'marker';
export const TYPE_CLUSTER = 'cluster';

// user identity types
export const IDENTITY_TYPE = {
  API_KEY: 'API_KEY',
  AUTH0_USER: 'AUTH0_USER',
  GUEST_USER: 'GUEST_USER',
};

// histogram
export const RANGE_DAY = 'Day';
export const RANGE_WEEK = 'Week';
export const RANGE_MONTH = 'Month';
export const RANGE_YEAR = 'Year';
export const HISTOGRAM_RANGES: HistogramRange[] = [RANGE_DAY, RANGE_WEEK, RANGE_MONTH, RANGE_YEAR];

// special customers
export const CHECK_N_GO = "Check 'n Go";
export const GOLDEN_1 = 'Golden 1 Credit Union';

// common
export const RESOLVED = 'resolved';

// ai
export const MOTION_TYPES = {
  DISABLED: 'disabled',
  REGULAR: 'regular',
  PERSON: 'person',
  VEHICLE: 'vehicle',
  DWELL_TIME: 'dwellTime',
  EXIT_ROUTE_OBSTRUCTION: 'exitRouteObstruction',
} as const;
export const MOTION_TYPE_TO_VMS_DETECTION_TYPE = {
  [MOTION_TYPES.PERSON]: 'people',
  [MOTION_TYPES.VEHICLE]: 'vehicle',
  [MOTION_TYPES.REGULAR]: null,
  [MOTION_TYPES.DISABLED]: null,
  [MOTION_TYPES.DWELL_TIME]: 'dwellTime',
  [MOTION_TYPES.EXIT_ROUTE_OBSTRUCTION]: null,
} as const;
export const MAX_OBSTRUCTION_DETECTION_CAMERAS = 3;
export const DWELL_TIME_MIN_INTERLINK_MAJOR_VERSION = 22;
export const DWELL_TIME_ZONES_MIN_INTERLINK_MAJOR_VERSION = 84;
export type MotionType = ValueOf<typeof MOTION_TYPES>;

// vms motion sensitivity levels
// high sensitivity -> low threshold (1) -> more motion events
// low sensitivity -> high threshold (3) -> less motion events
export const MOTION_SENSITIVITY_THRESHOLDS: { [key: string]: MotionSensitivity } = {
  LOW: 3,
  MEDIUM: 2,
  HIGH: 1,
};
export const DWELL_TIME_ZONE_TYPES = {
  MENU_BOARD: 'menu-board',
  PAYMENT_WINDOW: 'payment-window',
  SERVICE_WINDOW: 'service-window',
  // these two zones types are hard-coded values set by VMS when:
  // * `__legacy__`: updating a dwell time ROI using the legacy endpoint (/cameras/${camId}/ai)
  //   which doesn't support multiple ROIs or specifying metadata
  // * `__migrated_from_legacy_setting__`: reloading a dwell time ROI configured before
  //   VMS supported multiple ROIs
  LEGACY: '__legacy__',
  LEGACY_MIGRATED: '__migrated_from_legacy_setting__',
} as const;
export const CAMERA_REGION_TYPES = {
  EXIT_ROUTE_OBSTRUCTION: 'EXIT_ROUTE_OBSTRUCTION',
} as const;
export type DwellTimeZone = ValueOf<typeof DWELL_TIME_ZONE_TYPES>;
export type CameraRegionType = ValueOf<typeof CAMERA_REGION_TYPES>;
// routes
export const ROUTE_ALARMS = 'alarms';
export const ROUTE_EVENTS = 'events';
export const ROUTE_CLIPS = 'clips';
export const ROUTE_SETTINGS_APPS = 'settings/apps';
export const ROUTE_VIDEO = 'video';
export const WALL_ROUTE = 'wall';
export const VERIFICATION_ROUTE = 'verification';

// external link source
export const SOURCE_LINK = 'link';

// event standard for card rendering
export const EVENT_CARD_FIXED_WIDTH = 41;

// video player
export const PIN_EVENT_SOURCE_EVENT = 'event-click';
export const PIN_EVENT_SOURCE_RECEIPT = 'receipt-click';
export const PIN_EVENT_SOURCE_TIMELINE = 'timeline-click';
export const PIN_EVENT_SOURCE_CONTROL = 'player-control-click';
export const PIN_EVENT_SOURCE_TABLE = 'table-pin-click';

export const CAMERA_GROUP_FAVORITES = 'CAMERA_GROUP_FAVORITES';
export const CAMERA_GROUP_FAVORITES_INDEX = 1;
export const CAMERA_GROUP_RECENT = 'CAMERA_GROUP_RECENT';
export const ALARMS_PAGE = 'ALARMS_PAGE';
export const DASHBOARD_PAGE = 'DASHBOARD_PAGE';
export const EVENTS_PAGE = 'EVENTS_PAGE';
export const VIDEO_PAGE = 'VIDEO_PAGE';
export const CLIPS_PAGE = 'CLIPS_PAGE';
export const SETTINGS_PAGE = 'SETTINGS_PAGE';
export const DISCOVER_PAGE = 'DISCOVER_PAGE';
export const AGENTS_PAGE = 'AGENTS_PAGE';
export const DATASETS_PAGE = 'DATASETS_PAGE';
export const INVESTIGATIONS_PAGE = 'INVESTIGATIONS_PAGE';
export const DATA_EXPLORER_PAGE = 'DATA_EXPLORER_PAGE';
export const DATA_EXPLORER_VERIFEYE_PAGE = 'DATA_EXPLORER_VERIFEYE_PAGE';
export const VERIFEYE4_PAGE = 'VERIFEYE4_PAGE';
export const FEEDBACK_PAGE = 'FEEDBACK_PAGE';
export const REFERRAL_LINK = 'REFERRAL_LINK';
export const CONTEXT_BOOKMARK = 'CONTEXT_BOOKMARK';
export const CONTEXT_DOWNLOAD = 'CONTEXT_DOWNLOAD';
export const CONTEXT_SHARE = 'CONTEXT_SHARE';
export const CONTEXT_SAVETOCLOUD = 'CONTEXT_SAVETOCLOUD';
export const UPDATE_WINDOW_SIZE = 'UPDATE_WINDOW_SIZE';
export const DRAG = 'Drag';
export const CLICK = 'Click';
export const VIDEO_PLAYER = 'VIDEO_PLAYER';

export type EventContextType =
  | typeof CONTEXT_BOOKMARK
  | typeof CONTEXT_DOWNLOAD
  | typeof CONTEXT_SHARE
  | typeof CONTEXT_SAVETOCLOUD;

export const VIDEO_PLAYER_CONTEXTS = {
  HEAT_MAP: 'HEAT_MAP',
  MARKERS: 'MARKERS',
  MOTION_ALERTS: 'MOTION_ALERTS',
  MOTION_SEARCH: 'MOTION_SEARCH',
} as const;

export const CAMERA_STREAM = {
  SD: 'SD',
  HD: 'HD',
};

// local storage keys
export const LAST_CLIENT_IP = 'LAST_CLIENT_IP';
export const USER_PREFS = 'USER_PREFS';
export const DOWNLOAD_TYPE = 'DOWNLOAD_TYPE';
export const REDIRECT_PROPS = 'REDIRECT_PROPS';
export const STORED_REDUX_STORES = 'STORED_REDUX_STORES';
export const STILL_WATCHING_TIMER = 'STILL_WATCHING_TIMER';
export const STORED_CONTROLLER_STATES = 'STORED_CONTROLLER_STATES';
export const PUBLIC_STORED_CONTROLLER_STATES = 'PUBLIC_STORED_CONTROLLER_STATES';

// authentication constants
export const USER_LOGGED_IN = 'USER_LOGGED_IN';
export const LOG_IN_STATUS = 'LOG_IN_STATUS';
export const STORE_QUERY = 'STORE_QUERY';

// event constants
export const FETCH_EVENTS_TIMEOUT = 'FETCH_EVENTS_TIMEOUT';
export const UPDATE_SEARCH_STRING = 'UPDATE_SEARCH_STRING';
export const UPDATE_SEARCH_CAPSULES = 'UPDATE_SEARCH_CAPSULES';
export const CLEAR_ACTIVE_EVENT = 'CLEAR_ACTIVE_EVENT';
export const CLOSE_EVENT_PLAYER = 'CLOSE_EVENT_PLAYER';
export const UPDATE_EVENTS_OFFSET = 'UPDATE_EVENTS_OFFSET';
export const CLEAR_ALL_EVENTS = 'CLEAR_ALL_EVENTS';
export const EVENT_LIST_SINGLE_LAYOUT = 'EVENT_LIST_SINGLE_LAYOUT';

export const DELIVERY_EVENT_SUBTYPE = '3PD';
export const DELIVERY_EVENT_SUBTYPE_ALL = '3PD-all';
export const HEALTH_EVENT_SUBTYPE = 'health';
export const INVALID_EVENT_SUBTYPE = 'invalidData';
export const EVENT_SUBTYPE_SCHEDULED = ScheduledActivityType.ALARM_EXCEPTION;
export const TEST_ALARM_SUBTYPE = 'TEST-ALARM';
export const AUDIT_EVENT_ALARMS_SUBTYPE = 'audit-alarms';
export const AUDIT_EVENT_AUTH_SUBTYPE = 'audit-auth';
export const AUDIT_EVENT_EVENTS_SUBTYPE = 'audit-events';
export const AUDIT_EVENT_VIDEO_SUBTYPE = 'audit-video';
export const EXIT_DOOR_OBSTRUCTION_SUBTYPE = 'ai-doorObstruction';
export const DWELL_TIME_SUBTYPE = 'ai-dwellTime';

export const EVENT_STATUS_CLOSED = 'CLOSED';
export const EVENT_STATUS_OPEN = 'OPEN';

// event report constants
export const CREATE_REPORT_SUCCESS = 'CREATE_REPORT_SUCCESS';
export const CREATE_REPORT_ERROR = 'CREATE_REPORT_ERROR';

//event filter constants
export const SET_EVENT_SUBTYPES_FILTER = 'SET_EVENT_SUBTYPES_FILTER';
export const SET_FEATURE_TAB_FILTERS = 'SET_FEATURE_TAB_FILTERS';
export const CLEAR_FEATURE_TAB_FILTERS = 'CLEAR_FEATURE_TAB_FILTERS';
export const SET_EVENT_SUBTYPES_FILTER_REVENUE = 'SET_EVENT_SUBTYPES_FILTER_REVENUE';
export const SET_EVENT_TYPES_FILTER = 'SET_EVENT_TYPES_FILTER';
export const RESET_EVENT_FILTERS = 'RESET_EVENT_FILTERS';
export const TRANSACTION = 'TRANSACTION';
export const MOTION = 'MOTION';
export const SELECT_FILTER = 'SELECT_FILTER';
export const CLEAR_SELECTED_REPORT = 'CLEAR_SELECTED_REPORT';
export const FETCH_FILTERS = 'FETCH_FILTERS';
export const FETCH_FILTERS_SUCCESS = 'FETCH_FILTERS_SUCCESS';
export const FETCH_FILTERS_ERROR = 'FETCH_FILTERS_ERROR';
export const REPORT_UPDATE_SUCCESS = 'REPORT_UPDATE_SUCCESS';
export const REPORT_UPDATE_ERROR = 'REPORT_UPDATE_ERROR';
export const REPORT_TEMPLATE_UPDATE_SUCCESS = 'REPORT_TEMPLATE_UPDATE_SUCCESS';
export const REPORT_TEMPLATE_UPDATE_ERROR = 'REPORT_TEMPLATE_UPDATE_ERROR';
export const REPORT_TEMPLATE_DELETE_SUCCESS = 'REPORT_TEMPLATE_DELETE_SUCCESS';
export const REPORT_TEMPLATE_DELETE_ERROR = 'REPORT_TEMPLATE_DELETE_ERROR';
export const USER_NOTIFICATION_UPDATE_SUCCESS = 'USER_NOTIFICATION_UPDATE_SUCCESS';
export const USER_NOTIFICATION_UPDATE_ERROR = 'USER_NOTIFICATION_UPDATE_ERROR';
export const USER_NOTIFICATION_DELETE_SUCCESS = 'USER_NOTIFICATION_DELETE_SUCCESS';
export const USER_NOTIFICATION_DELETE_ERROR = 'USER_NOTIFICATION_DELETE_ERROR';
export const USER_NOTIFICATION_REAL_TIME_THRESHOLD = 'USER_NOTIFICATION_REAL_TIME_THRESHOLD';
export const REAL_TIME_NOTIFICATION_PUSH = 'REAL_TIME_NOTIFICATION_PUSH';
export const REAL_TIME_NOTIFICATION_SMS = 'REAL_TIME_NOTIFICATION_SMS';
export const REAL_TIME_NOTIFICATION_EMAIL = 'REAL_TIME_NOTIFICATION_EMAIL';
export const SET_REVENUE_FILTER = 'SET_REVENUE_FILTER';
export const TOGGLE_REVENUE_FILTER = 'TOGGLE_REVENUE_FILTER';
export const ENABLE_SAVED_FILTER = 'ENABLE_SAVED_FILTER';
export const RESET_SAVED_FILTER = 'RESET_SAVED_FILTER';
export const UPDATE_SEARCH_OPERATOR = 'UPDATE_SEARCH_OPERATOR';

// event item constants
export const EVENT_ITEM_HOVER = 'EVENT_ITEM_HOVER';

// filter constants
export const SET_FILTER = 'SET_FILTER';
export const LOCAL_FILTERS = 'LOCAL_FILTERS';

// location constants
export const GET_LOCATION_LIST = 'GET_LOCATION_LIST';
export const GET_LOCATION_BY_ID = 'GET_LOCATION_BY_ID';
export const MAP_CAMERA_GROUPS = 'MAP_CAMERA_GROUPS';
export const CAMERA_NAME_UPDATED_FAILURE = 'CAMERA_NAME_UPDATED_FAILURE';
export const CAMERA_NAME_UPDATED_SUCCESS = 'CAMERA_NAME_UPDATED_SUCCESS';
export const CAMERA_MOTION_UPDATED_SUCCESS = 'CAMERA_MOTION_UPDATED_SUCCESS';
export const CAMERA_ROI_UPDATED_SUCCESS = 'CAMERA_ROI_UPDATED_SUCCESS';
export const CAMERA_AI_UPDATED_SUCCESS = 'CAMERA_AI_UPDATED_SUCCESS';
export const CAMERA_AI_DWELL_TIME_UPDATED_SUCCESS = 'CAMERA_AI_DWELL_TIME_UPDATED_SUCCESS';
export const CAMERA_PTZ_ENABLED_UPDATED_SUCCESS = 'CAMERA_PTZ_ENABLED_UPDATED_SUCCESS';
export const CAMERA_TAGS_UPDATED_SUCCESS = 'CAMERA_TAGS_UPDATED_SUCCESS';
export const KIOSK_LOCATION = 'KIOSK_LOCATION';
export const SELECTED_LOCATION = 'SELECTED_LOCATION';
export const UPDATE_LOCATION_MOTION_ALARM_SCHEDULE = 'UPDATE_LOCATION_MOTION_ALARM_SCHEDULE';
export const GET_LATEST_MOTION_SCHEDULE_INFO = 'GET_LATEST_MOTION_SCHEDULE_INFO';
export const UPDATE_LOCATION_ALARM_CONFIG = 'UPDATE_LOCATION_ALARM_CONFIG';
export const CREATE_LOCATION_ALARM_CONTACT = 'CREATE_LOCATION_ALARM_CONTACT';
export const REMOVE_LOCATION_ALARM_CONTACT = 'REMOVE_LOCATION_ALARM_CONTACT';
export const UPDATE_LOCATION_ALARM_CONTACT = 'UPDATE_LOCATION_ALARM_CONTACT';
export const UPDATE_LOCATIONS_TAGS = 'UPDATE_LOCATIONS_TAGS';
export const UPDATE_LOCATION_TAGS = 'UPDATE_LOCATION_TAGS';
export const UPDATE_DEVICE_TAGS = 'UPDATE_DEVICE_TAGS';
export const UPDATE_DEVICES = 'UPDATE_DEVICES';
export const UPDATE_MULTI_SITE_DEVICES_FEATURES = 'UPDATE_MULTI_SITE_DEVICES_FEATURES';

export const UPDATE_CLOUD_CAMERA_RETENTION = 'UPDATE_CLOUD_CAMERA_RETENTION';

// users constants
export const APPEND_USERS = 'APPEND_USERS';
export const CLEAR_CURRENT_USER = 'CLEAR_CURRENT_USER';
export const FETCH_CURRENT_USER_ERROR = 'FETCH_CURRENT_USER_ERROR';
export const FETCH_CURRENT_USER_SUCCESS = 'FETCH_CURRENT_USER_SUCCESS';
export const GET_DIGEST_SETTINGS = 'GET_DIGEST_SETTINGS';
export const GET_REAL_TIME_SETTINGS = 'GET_REAL_TIME_SETTINGS';
export const GET_USERS_ERROR = 'GET_USERS_ERROR';
export const RECV_USERS = 'RECV_USERS';
export const UPDATE_CURRENT_USER_ERROR = 'UPDATE_CURRENT_USER_ERROR';
export const UPDATE_CURRENT_USER_SUCCESS = 'UPDATE_CURRENT_USER_SUCCESS';
export const UPDATE_DIGEST_SETTINGS = 'UPDATE_DIGEST_SETTINGS';
export const UPDATE_DIGEST_SETTINGS_ERROR = 'UPDATE_DIGEST_SETTINGS_ERROR';
export const UPDATE_MFA = 'UPDATE_MFA';
export const UPDATE_REAL_TIME_SETTINGS_ERROR = 'UPDATE_REAL_TIME_SETTINGS_ERROR';
export const UPDATE_REAL_TIME_SETTINGS_SUCCESS = 'UPDATE_REAL_TIME_SETTINGS_SUCCESS';
export const USER_ADDED = 'USER_ADDED';
export const USER_ADDED_ERROR = 'USER_ADDED_ERROR';
export const USER_REMOVED = 'USER_REMOVED';
export const USER_REMOVED_ERROR = 'USER_REMOVED_ERROR';
export const ACCEPT_EULA = 'ACCEPT_EULA';
export const DEFAULT_VERIFICATION_WORKFLOW_ID = 'alarms';

// user roles constants
export const USER_ROLE_CAMERA_RESTRICTIONS = 'USER_ROLE_CAMERA_RESTRICTIONS';

// video player constants
export const TOGGLE_SNAPSHOT = 'TOGGLE_SNAPSHOT';
export const FFWD_RATE = 'FFWD_RATE';
export const PIN_EVENT = 'PIN_EVENT';
export const LIVE_PLAYER_CONFIG = 'LIVE_PLAYER_CONFIG';
export const ARCHIVE_PLAYER_CONFIG = 'ARCHIVE_PLAYER_CONFIG';

// cameras constants
export const UPDATE_CAMERA_ANNOTATION = 'UPDATE_CAMERA_ANNOTATION';
export const UPDATE_CAMERA_LINK = 'UPDATE_CAMERA_LINK';
export const INSERT_CAMERA_LINK = 'INSERT_CAMERA_LINK';
export const DELETE_CAMERA_LINK = 'DELETE_CAMERA_LINK';
export const FAVOURITE_CAMERAS = 'FAVOURITE_CAMERAS';
export const UPDATE_CAMERA_GROUP = 'UPDATE_CAMERA_GROUP';
export const UPDATE_LOCATION_REP_CAMERA_GROUPS = 'UPDATE_LOCATION_REP_CAMERA_GROUPS';
export const LOCATION_REP_CAMERA_GROUPS_FAILED = 'LOCATION_REP_CAMERA_GROUPS_FAILED';
export const UPDATE_CAMERA_GROUPS = 'UPDATE_CAMERA_GROUPS';
export const CREATE_CAMERA_GROUP = 'CREATE_CAMERA_GROUP';
export const DELETE_CAMERA_GROUP = 'DELETE_CAMERA_GROUP';
export const UPDATE_CAMERA_MOTION_ALARM_DATA = 'UPDATE_CAMERA_MOTION_ALARM_DATA';
export const VISIBILITY_SHARED = 'shared';

// event bookmark
export const CREATE_EVENT = 'CREATE_EVENT';
export const VISIT_PAGE = 'VISIT_PAGE';

// video page
export const SET_PLAYING_CAMERAS = 'SET_PLAYING_CAMERAS';
export const VIDEO_PAGE_LIVE_FLAG = 'VIDEO_PAGE_LIVE_FLAG';

// preferences
export const SYNC_PREFERENCES = 'SYNC_PREFERENCES';
export const SET_TIME_FORMAT = 'SET_TIME_FORMAT';
export const SET_DEFAULT_TO_HD = 'SET_DEFAULT_TO_HD';
export const SET_SHOW_EXPERIMENTAL_FEATURES = 'SET_SHOW_EXPERIMENTAL_FEATURES';
export const SET_SHOW_ALL_EVENT_THUMBNAILS = 'SET_SHOW_ALL_EVENT_THUMBNAILS';
export const SET_CONTEXTINE_CREDS = 'SET_CONTEXTINE_CREDS';
export const SET_PLAY_LIST_SOURCE = 'SET_PLAY_LIST_SOURCE';
export const SET_EVENT_LIST_LAYOUT_TYPE = 'SET_EVENT_LIST_LAYOUT_TYPE';
export const EVENT_LIST_LAYOUT_TYPE = 'EVENT_LIST_LAYOUT_TYPE';
export const USE_VIDEO_SERVER_RELAY = 'USE_VIDEO_SERVER_RELAY';
export const FETCH_PREFERENCES = 'FETCH_PREFERENCES';
export const SET_WIDGET_AUTO_REFRESH = 'SET_WIDGET_AUTO_REFRESH';
export const SET_SHOW_EVENT_PROGRESS_IN_VIDEO_PLAYER = 'SET_SHOW_EVENT_PROGRESS_IN_VIDEO_PLAYER';

// global
export const UPDATE_LOCALE = 'UPDATE_LOCALE';

// contextine
export const CONTEXTINE_CREDS = 'CONTEXTINE_CREDS';

// localization
export const LANGUAGE = 'LANGUAGE';

// polling
export const CONNECT_POLL_START = 'CONNECT_POLL_START';
export const CONNECT_POLL_STOP = 'CONNECT_POLL_STOP';
export const CLOUD_POLL_START = 'CLOUD_POLL_START';
export const CLOUD_POLL_STOP = 'CLOUD_POLL_STOP';

// date/time
export const SECONDS = 'SECONDS';

// table-view
export const TABLEVIEW_SETTINGS = 'TABLEVIEW_SETTINGS';

// notifications
export const BINDING_ID = 'BINDING_ID';
export const VIEW_NOTIFICATION = 'VIEW_NOTIFICATION';
export const NOTIFICATIONS_TOTAL_COUNT_DISPLAY_LIMIT = 999;
export const NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT = 99;

// clips + audit events
export const VISIBILITY_PUBLIC = 'public';
export const VISIBILITY_PRIVATE = 'private';
export const VISIBILITY_SUPPORT = 'support';
export type VISIBILITY_TYPE =
  typeof VISIBILITY_PUBLIC | typeof VISIBILITY_PRIVATE | typeof VISIBILITY_SUPPORT;

// modal
export const MODAL_CONTENT_TYPE = {
  TABLE: 'table',
  EVENT_PLAYER: 'event-player',
  SAVE_PANEL: 'save-panel',
  SNAPSHOT: 'snapshot',
};

// Reusable Regex patterns
export const REGEX_ILLEGAL_CHARACTERS = /[-[\]{}()*+?.,\\^$|#\s]/g;
export const REGEX_RESERVED_CHARACTERS = /[[\]{}()*+?.\\/^$|]/g;
export const REGEX_INCLUDES_NUMBER = /(?=.*[0-9])/;
export const REGEX_INCLUDES_SPECIAL_CHARACTER = /(?=.*[!@#$%^&*])/;
export const REGEX_INCLUDES_UPPERCASE_LETTER = /(?=.*[A-Z])/;
export const REGEX_INCLUDES_LOWERCASE_LETTER = /(?=.*[a-z])/;

export const MODAL_TARGET_ID = 'modal-container';
/** Body-level portal above Radix `z-50` dialogs (Aspen widget settings). */
export const STACKED_MODAL_TARGET_ID = 'stacked-modal-container';

export const MDI_VIEW_BOX = '0 0 24 24';

// Thumbnail types
export const THUMBNAIL_TYPE_CAMERA = 'camera' as const;
export const THUMBNAIL_TYPE_EVENT = 'event' as const;
export const THUMBNAIL_TYPE_SNAPSHOT = 'snapshot' as const;
export const THUMBNAIL_TYPE_PUBLIC_CAMERA = 'publicCamera' as const;

export type ThumbnailType =
  | typeof THUMBNAIL_TYPE_CAMERA
  | typeof THUMBNAIL_TYPE_EVENT
  | typeof THUMBNAIL_TYPE_SNAPSHOT
  | typeof THUMBNAIL_TYPE_PUBLIC_CAMERA;

export const EULA_PATH_BY_LOCALE = {
  en: 'solink-software-license-agreement',
  es: 'acuerdo-de-licencia-de-software',
  fr: 'contrat-de-licence-logiciel',
};

export type DeviceStatus = ValueOf<typeof DEVICE_STATUS>;
export const DEVICE_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
} as const;

export const DEVICE_TYPE = {
  UNKNOWN: 'unknown',
  QNAP: 'qnap',
  UBUNTU: 'ubuntu',
  HIK: 'hik',
  ANDROID: 'android',
  DATA: 'data',
  CONTAINER: 'container',
  CAMERA: 'camera',
} as const;

export const LOCATION_MISSED_CHECKIN_THRESHOLD_MS = 5 * 60 * 1000;

export const PLAYER_SUBTYPE_HLS_MP4 = 'HLS mp4';
export const PLAYER_SUBTYPE_HLS_TS = 'HLS ts';

// UNITS OF TIME
export const MINUTES_IN_MILLISECONDS = 60 * 1000;
export const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

export const VIDEO_PLAYER_TRANSITION_TIME = 500;

export const VR_PLAYER_NATIVE_WIDTH = 1600;
export const VR_PLAYER_NATIVE_HEIGHT = 900;
export const VR_PLAYER_ASPECT_RATIO = VR_PLAYER_NATIVE_WIDTH / VR_PLAYER_NATIVE_HEIGHT;

export const DEFAULT_360_VIEW_TYPES = ['dewarped', 'pinhole'] as const;

//  VIDEO WALL PARARMS
export const ID_QUERY = 'id';
export const SOURCE_QUERY = 'source';

// Alarm PIN Disarm Methods
export const PINPAD_MODE = 'PINPAD' as const;
export const DTMF_MODE = 'DTMF' as const;

// Global camera tags
export const GLOBAL_CAMERA_TAGS = {
  THREE_SIXTY: '360',
  INDOOR: 'INDOOR',
  OUTDOOR: 'OUTDOOR',
  UNMONITORED: 'UNMONITORED',
  VIDEO_ALARM_TABLET: 'VIDEO_ALARM_TABLET',
  LOITERING: 'LOITERING',
  SPEAKER: 'SPEAKER',
  REMOTE_GUARDING: 'REMOTE_GUARDING',
  INTRUSION_DETECTION: 'INTRUSION_DETECTION',
  STAY: 'STAY',
  PVM: 'PVM',
} as const;

export const SUPPORTED_SPEAKERS = [
  'speaker0',
  'speaker1',
  'speaker2',
  'speaker3',
  'speaker4',
  'speaker5',
  'speaker6',
  'speaker7',
  'speaker8',
  'speaker9',
] as const;

export type SupportedSpeaker = (typeof SUPPORTED_SPEAKERS)[number];

export const TYPE_OF_ALARM_EXCEPTION = {
  ARM: 'ARM',
  DISARM: 'DISARM',
} as const;

export type TypeOfAlarmException = ValueOf<typeof TYPE_OF_ALARM_EXCEPTION>;

/** speed test quality thresholds */
export const POOR_UPLOAD_SPEED = 600 * 1000;
export const FAIR_UPLOAD_SPEED = 1500 * 1000;

// Line Cross Events
export const EVENT_FIELD_LINE_CROSS_IN = 'details.lineCrossingsIn';
export const EVENT_FIELD_LINE_CROSS_OUT = 'details.lineCrossingsOut';
export const EVENT_FIELD_LINE_CROSS_LEFT = 'details.lineCrossingsLeft';
export const EVENT_FIELD_LINE_CROSS_RIGHT = 'details.lineCrossingsRight';
export const EVENT_FIELD_LINE_CROSS_UP = 'details.lineCrossingsUp';
export const EVENT_FIELD_LINE_CROSS_DOWN = 'details.lineCrossingsDown';
export const EVENT_FIELD_LINE_CROSS_TOTAL = 'details.lineCrossingsTotal';
export const EVENT_FIELD_STORE_OCCUPANCY_COUNT = 'details.storeOccupancyCount';

export const EVENT_FIELD_TOTAL_AMOUNT = 'details.Total amount';

export const MAX_NUMBER_OF_SELECTED_CAMERAS = 100;

export const EVENT_FIELD_VERIFICATION_CONCLUSION = 'details.verificationConclusion';
export const EVENT_FIELD_VERIFICATION_CONCLUSION_REASON = 'details.verificationConclusionReason';
export const EVENT_FIELD_VERIFICATION_CONCLUSION_KEYWORD = 'details.verificationConclusion.keyword';
export const EVENT_FIELD_VERIFICATION_CONCLUSION_REASON_KEYWORD =
  'details.verificationConclusionReason.keyword';

// Assets URLs
export const STATIC_ASSETS_URL = `${settings.publicUrl}/vd-web/static-assets`;
export const MARKETING_ASSETS_URL = `${STATIC_ASSETS_URL}/marketing`;
